/**
 * /mail/domains — Resend-pattern domain onboarding (OCP-130).
 *
 * Flow for a new customer:
 *   1. List page — shows existing domains with status badges.
 *      "+ Add domain" CTA in the header.
 *   2. Add modal — single domain input → POST → modal SWAPS to the
 *      DNS records table with copy-per-row + "I added them, verify"
 *      button.
 *   3. After verify click → if verified, banner flips green +
 *      "domain ready to send from" + list refreshes. If still
 *      pending, banner shows "DNS not yet detected, recheck in
 *      5 minutes" + Verify button retries.
 *   4. Per-row Show records (re-opens the records table for already-
 *      added domains), Verify, Delete (with confirmation; auto-
 *      tenant-subdomain → Delete is disabled with a tooltip).
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../i18n';

import { MailReadOnlyNotice } from '../components/MailReadOnlyNotice';
import { ApiError, api } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';
import { buildZoneFile } from '../lib/dns-zone-file';
import { useTransientState } from '../lib/useTransientState';
import { fmtDate } from '../lib/format-time';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  priority: string | null;
}

interface DomainSummary {
  id: string;
  domain: string;
  status: string;
  type: string | null;
  created_at: string | null;
  last_checked_at: string | null;
  /**
   * Raw OMS verification diagnostic (OCP-226 / OMS-75). Format:
   * `last-checked=<iso>; primary=<dkim-status>; failover=<dkim-status>`.
   * Null when OMS hasn't run a check yet OR is pre-OMS-75.
   */
  verification_detail: string | null;
  /**
   * OCP-230 / OMS-76 — DKIM provisioning mode. `byodkim-orboto-managed`
   * (new, 1 TXT) or `easy-dkim-ses-managed` (legacy, 3 CNAMEs). Null on
   * pre-OMS-76 deploys; falls back to legacy diagnostic wording.
   */
  dkim_mode: string | null;
  /** OCP-219 / OMS-72 — per-domain open-tracking opt-in (default off). */
  open_tracking_enabled: boolean;
  /**
   * OCP-612 / OMS-113 - Return-Path (SES custom MAIL FROM) state. The
   * `bounces.<domain>` MX + SPF records in the table set it; without it
   * the envelope sender stays amazonses.com, SPF does not align with the
   * From domain and DMARC holds on DKIM alone (spam placement). `state`
   * is the combined worst-region value; `unknown` = OMS has not reported
   * yet (recheck fills it). Optional so a pre-OCP-612 API still renders.
   */
  mail_from_status?: MailFromStatus | null;
}

type MailFromRegionState =
  | 'not-configured'
  | 'pending'
  | 'success'
  | 'failed'
  | 'temporary-failure';

interface MailFromStatus {
  primary: MailFromRegionState | null;
  failover: MailFromRegionState | null;
  state: MailFromRegionState | 'unknown';
}

interface DomainDetail extends DomainSummary {
  dns_records: DnsRecord[];
}

interface ListResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  domains: DomainSummary[];
}

/**
 * Records-modal banner after an action. `return-path-*` (OCP-612): DKIM
 * is verified but the Return-Path is not yet, so the domain sends but
 * lands in spam more often - the banner says what to do next.
 */
type RecordsFlash =
  | 'fresh'
  | 'pending'
  | 'verified'
  | 'return-path-pending'
  | 'return-path-missing'
  | null;

type Modal =
  | { kind: 'add' }
  | { kind: 'records'; detail: DomainDetail; flash: RecordsFlash }
  | { kind: 'confirm-delete'; domain: DomainSummary }
  | null;

function flashAfterVerify(detail: DomainDetail): RecordsFlash {
  const dkimVerified = detail.status === 'verified' || detail.status === 'success';
  if (!dkimVerified) return 'pending';
  const rp = detail.mail_from_status?.state ?? 'unknown';
  if (rp === 'success' || rp === 'unknown') return 'verified';
  if (rp === 'not-configured' || rp === 'failed') return 'return-path-missing';
  return 'return-path-pending';
}

export function MailDomainsPage() {
  const { t } = useTranslation('mail-domains');
  const [data, setData] = useState<ListResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  // OCP-545 - the mail setup is owner/admin territory; the API 403s the rest.
  const { canWrite } = useMailWriteAccess();

  const load = useCallback(async (signal?: AbortSignal) => {
    setState('loading');
    setErrorDetail(null);
    try {
      const r = await api<ListResponse>('/v1/portal/mail/domains', { signal });
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setErrorDetail(
        err instanceof ApiError && err.status === 502
          ? t('error.unreachable')
          : t('error.load'),
      );
      setState('error');
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  async function addDomain(domain: string): Promise<void> {
    try {
      const detail = await api<DomainDetail>('/v1/portal/mail/domains', {
        method: 'POST',
        body: { domain },
      });
      setModal({ kind: 'records', detail, flash: 'fresh' });
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorDetail(t('error.limit'));
      } else if (err instanceof ApiError && err.status === 400) {
        setErrorDetail(t('error.invalid'));
      } else {
        setErrorDetail(t('error.add'));
      }
      setModal(null);
    }
  }

  async function verifyDomain(domainId: string): Promise<void> {
    try {
      const detail = await api<DomainDetail>(
        `/v1/portal/mail/domains/${domainId}/verify`,
        { method: 'POST' },
      );
      setModal({
        kind: 'records',
        detail,
        flash: flashAfterVerify(detail),
      });
      void load();
    } catch (err) {
      setErrorDetail(
        err instanceof ApiError && err.status === 404
          ? t('error.notFound')
          : t('error.verify'),
      );
    }
  }

  async function deleteDomain(domainId: string): Promise<void> {
    try {
      await api(`/v1/portal/mail/domains/${domainId}`, { method: 'DELETE' });
      setModal(null);
      setData((prev) =>
        prev ? { ...prev, domains: prev.domains.filter((d) => d.id !== domainId) } : prev,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorDetail(
          t('error.autoManagedDelete'),
        );
      } else {
        setErrorDetail(t('error.remove'));
      }
      setModal(null);
    }
  }

  // Returns the authoritative flag from the echoed row (true/false), or
  // null on failure. The modal drives the checkbox + inline confirmation
  // off this — OCP-277 (the toast used to render behind the modal).
  async function setOpenTracking(
    domainId: string,
    enabled: boolean,
  ): Promise<boolean | null> {
    try {
      const detail = await api<DomainDetail>(`/v1/portal/mail/domains/${domainId}`, {
        method: 'PATCH',
        body: { open_tracking_enabled: enabled },
      });
      // Reflect the server-confirmed flag in the open modal + the list row.
      setModal((m) =>
        m && m.kind === 'records' && m.detail.id === domainId ? { ...m, detail } : m,
      );
      setData((prev) =>
        prev
          ? {
              ...prev,
              domains: prev.domains.map((d) =>
                d.id === domainId
                  ? { ...d, open_tracking_enabled: detail.open_tracking_enabled }
                  : d,
              ),
            }
          : prev,
      );
      return detail.open_tracking_enabled;
    } catch (err) {
      setErrorDetail(
        err instanceof ApiError && err.status === 404
          ? t('error.notFound')
          : t('error.tracking'),
      );
      return null;
    }
  }

  function openRecordsFor(d: DomainSummary): void {
    // Re-fetch the records for an existing domain by re-running
    // verify — that endpoint also returns the current DNS-records
    // snapshot (the verify call is idempotent on the DB state, only
    // mutates SES poll status). Cheap + avoids a separate GET-detail.
    void verifyDomain(d.id);
  }

  if (data && !data.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{t('unavailable.title')}</h2>
        <p className="mt-1">{t('unavailable.body')}</p>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-5 flex items-start justify-between gap-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('intro')}
        </p>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModal({ kind: 'add' })}
            className="shrink-0 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {t('addDomain')}
          </button>
        )}
      </header>

      <MailReadOnlyNotice />

      {errorDetail && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}

      {state === 'loading' && (
        <p className="text-sm text-gray-500">{t('loading')}</p>
      )}

      {state === 'idle' && data && data.domains.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          {t('empty')}
        </div>
      )}

      {state === 'idle' && data && data.domains.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('table.domain')}</th>
                <th className="px-3 py-2">{t('table.status')}</th>
                <th className="px-3 py-2">{t('table.type')}</th>
                <th className="px-3 py-2">{t('table.created')}</th>
                <th className="px-3 py-2 text-right">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.domains.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{d.domain}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={d.status} />
                    <VerificationHint
                      status={d.status}
                      detail={d.verification_detail}
                      dkimMode={d.dkim_mode}
                    />
                    <ReturnPathChip domain={d} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {d.type ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {d.created_at ? formatDate(d.created_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openRecordsFor(d)}
                      className="mr-3 text-xs text-brand-500 hover:underline"
                    >
                      {t('showRecords')}
                    </button>
                    {d.type === 'auto-tenant-subdomain' ? (
                      <span
                        title={t('autoManagedTitle')}
                        className="text-xs text-gray-400"
                      >
                        {t('autoManaged')}
                      </span>
                    ) : (
                      canWrite && (
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'confirm-delete', domain: d })}
                          className="text-xs text-red-700 dark:text-red-300 hover:underline"
                        >
                          {t('remove')}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.kind === 'add' && (
        <AddDomainModal
          onCancel={() => setModal(null)}
          onSubmit={(d) => void addDomain(d)}
        />
      )}
      {modal?.kind === 'records' && (
        <DnsRecordsModal
          detail={modal.detail}
          flash={modal.flash}
          onVerify={() => void verifyDomain(modal.detail.id)}
          onSetOpenTracking={(enabled) => setOpenTracking(modal.detail.id, enabled)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'confirm-delete' && (
        <ConfirmDeleteModal
          domain={modal.domain}
          onCancel={() => setModal(null)}
          onConfirm={() => void deleteDomain(modal.domain.id)}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'verified' || status === 'success'
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/50'
      : status === 'failed'
        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/50'
        : status === 'temporary-failure'
          ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900/50'
          : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 text-xs ${tone}`}>
      {status}
    </span>
  );
}

/**
 * OCP-226 / OCP-230 — render the raw OMS verification diagnostic as a
 * friendly one-liner under the StatusPill when status is failed /
 * temporary-failure. Operator-friendly tooltip shows the raw string
 * for copy-paste into support tickets.
 *
 * OMS-75 format: `last-checked=<iso>; primary=<dkim>; failover=<dkim>`.
 * `<dkim>` values: `PENDING | SUCCESS | TEMPORARY_FAILURE | FAILED`.
 *
 * Wording branches on `dkimMode` (OMS-76):
 *   - `byodkim-orboto-managed` → single DKIM TXT record (orboto-managed key)
 *   - `easy-dkim-ses-managed`  → 3 DKIM CNAMEs (legacy provider-managed key)
 *   - null / unknown           → legacy phrasing as fallback
 *
 * Gracefully no-ops when:
 *   - status is happy (verified / success / pending / not_started),
 *   - detail is null (pre-OMS-75 OMS, or never verified yet),
 *   - shape doesn't parse (future diagnostic shape, render raw).
 */
function VerificationHint({
  status,
  detail,
  dkimMode,
}: {
  status: string;
  detail: string | null;
  dkimMode: string | null;
}) {
  if (!detail) return null;
  if (status !== 'failed' && status !== 'temporary-failure') return null;

  const message = friendlyVerificationMessage(detail, dkimMode);
  return (
    <div
      className="mt-1 max-w-md text-xs leading-snug text-gray-500"
      title={detail}
      data-testid="verification-hint"
    >
      {message}
    </div>
  );
}

function friendlyVerificationMessage(
  detail: string,
  dkimMode: string | null,
): string {
  const parts = Object.fromEntries(
    detail
      .split(';')
      .map((s) => s.trim().split('='))
      .filter((kv): kv is [string, string] => kv.length === 2)
      .map(([k, v]) => [k.toLowerCase(), v.toUpperCase()]),
  );
  const primary = parts.primary;
  const failover = parts.failover;
  const isByodkim = dkimMode === 'byodkim-orboto-managed';
  const ht = (key: string, opts?: Record<string, unknown>) => i18n.t(`mail-domains:hint.${key}`, opts);
  const recordsLabel = isByodkim ? ht('recordsTxt') : ht('recordsCname');
  const bothFailedHint = isByodkim ? ht('bothFailedTxt') : ht('bothFailedCname');
  const oneFailedHint = isByodkim ? ht('oneFailedTxt') : ht('oneFailedCname');

  if (primary === 'FAILED' && failover === 'FAILED') {
    return ht('bothFailed', { records: recordsLabel, hint: bothFailedHint });
  }
  if (primary === 'FAILED' || failover === 'FAILED') {
    const region = primary === 'FAILED' ? ht('regionPrimary') : ht('regionFailover');
    return ht('oneFailed', { records: recordsLabel, region, hint: oneFailedHint });
  }
  if (
    primary === 'TEMPORARY_FAILURE' ||
    failover === 'TEMPORARY_FAILURE'
  ) {
    return ht('temporary', { records: recordsLabel });
  }
  // Unknown shape — show the raw diagnostic so the operator still has
  // something to act on.
  return detail;
}

/**
 * OCP-612 - Return-Path (MAIL FROM) chip + one-line next step. Rendered
 * only for customer-owned domains once DKIM is verified: before that the
 * DKIM records are the blocker, and auto-provisioned tenant subdomains
 * are set up by the platform, nothing for the customer to publish.
 */
function ReturnPathChip({ domain, compact = true }: { domain: DomainSummary; compact?: boolean }) {
  if (domain.type === 'auto-tenant-subdomain') return null;
  const dkimVerified = domain.status === 'verified' || domain.status === 'success';
  if (!dkimVerified) return null;
  const status = domain.mail_from_status ?? null;
  const state = status?.state ?? 'unknown';
  const { label, tone, message } = returnPathCopy(state);
  const regions = status
    ? `primary: ${status.primary ?? i18n.t('mail-domains:returnPath.notReported')}; failover: ${status.failover ?? i18n.t('mail-domains:returnPath.notReported')}`
    : i18n.t('mail-domains:returnPath.notReportedYet');
  return (
    <div className={compact ? 'mt-1' : ''} data-testid="return-path">
      <span
        className={`inline-flex rounded border px-1.5 py-0.5 text-xs ${tone}`}
        title={`Return-Path (bounces.${domain.domain}) - ${regions}`}
        data-testid="return-path-chip"
      >
        Return-Path {label}
      </span>
      {message && (
        <div className="mt-1 max-w-md text-xs leading-snug text-gray-500" data-testid="return-path-hint">
          {message}
        </div>
      )}
    </div>
  );
}

function returnPathCopy(state: MailFromStatus['state']): {
  label: string;
  tone: string;
  message: string | null;
} {
  const green =
    'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/50';
  const amber =
    'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900/50';
  const red =
    'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/50';
  const gray =
    'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  switch (state) {
    case 'success':
      return { label: i18n.t('mail-domains:returnPath.active'), tone: green, message: null };
    case 'pending':
    case 'temporary-failure':
      return {
        label: i18n.t('mail-domains:returnPath.pending'),
        tone: amber,
        message: i18n.t('mail-domains:returnPath.pendingMessage'),
      };
    case 'failed':
      return {
        label: i18n.t('mail-domains:returnPath.failed'),
        tone: red,
        message: i18n.t('mail-domains:returnPath.failedMessage'),
      };
    case 'not-configured':
      return {
        label: i18n.t('mail-domains:returnPath.notConfigured'),
        tone: amber,
        message: i18n.t('mail-domains:returnPath.notConfiguredMessage'),
      };
    default:
      return {
        label: i18n.t('mail-domains:returnPath.notChecked'),
        tone: gray,
        message: i18n.t('mail-domains:returnPath.notCheckedMessage'),
      };
  }
}

function AddDomainModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (domain: string) => void;
}) {
  const { t } = useTranslation('mail-domains');
  const [domain, setDomain] = useState('');
  const trimmed = domain.trim().toLowerCase();
  const looksValid = /^[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmed) && trimmed.length >= 3;
  return (
    <ModalShell title={t('add.title')} titleId="add-domain-title" onCancel={onCancel}>
      <label className="block">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{t('add.label')}</span>
        <span className="block text-xs text-gray-600 dark:text-gray-400">
          {t('add.helpBefore')}{' '}
          <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">mail.yourdomain.com</code>{' '}
          {t('add.helpOr')}{' '}
          <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">send.yourdomain.com</code>.
        </span>
        <input
          type="text"
          autoFocus
          maxLength={253}
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="mail.yourdomain.com"
          className="mt-2 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('common:action.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onSubmit(trimmed)}
          disabled={!looksValid}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-300"
        >
          {t('add.submit')}
        </button>
      </div>
    </ModalShell>
  );
}

function DnsRecordsModal({
  detail,
  flash,
  onVerify,
  onSetOpenTracking,
  onClose,
}: {
  detail: DomainDetail;
  flash: RecordsFlash;
  onVerify: () => void;
  onSetOpenTracking: (enabled: boolean) => Promise<boolean | null>;
  onClose: () => void;
}) {
  const { t } = useTranslation('mail-domains');
  const isVerified = detail.status === 'verified' || detail.status === 'success';
  const hasRecords = detail.dns_records.length > 0;
  // OCP-612 - a verified domain still needs a recheck while its
  // Return-Path is not confirmed (the recheck re-triggers the provider
  // side), so the button stays until both are green.
  const returnPathState = detail.mail_from_status?.state ?? 'unknown';
  const returnPathDone = returnPathState === 'success';
  const hasBouncesRecords = detail.dns_records.some((r) => r.name.startsWith('bounces.'));
  const showVerify = !isVerified || (detail.type !== 'auto-tenant-subdomain' && !returnPathDone);
  const [zoneCopied, setZoneCopied] = useTransientState(false, 1500);
  // OCP-277 — inline confirmation INSIDE the modal (the page-level toast
  // rendered behind the overlay). Driven by the server-confirmed flag.
  const [trackingBusy, setTrackingBusy] = useState(false);
  // OCP-545 - open tracking is a mail-configuration change, so it follows
  // the same role gate as the rest of this page.
  const { canWrite: canChangeTracking } = useMailWriteAccess();
  const [trackingFlash, setTrackingFlash] = useState<string | null>(null);

  const zoneFile = hasRecords
    ? buildZoneFile({ domain: detail.domain, records: detail.dns_records })
    : '';

  function copyZoneFile() {
    if (!zoneFile || typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(zoneFile);
    setZoneCopied(true);
  }

  function downloadZoneFile() {
    if (!zoneFile) return;
    const blob = new Blob([zoneFile], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${detail.domain}.zone`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <ModalShell
      title={detail.domain}
      titleId="records-title"
      onCancel={onClose}
      wide
    >
      {flash === 'verified' && (
        <Flash tone="green">
          {t('flash.verified', { domain: detail.domain })}
        </Flash>
      )}
      {flash === 'pending' && (
        <Flash tone="amber">
          {t('flash.pending')}
        </Flash>
      )}
      {flash === 'fresh' && (
        <Flash tone="neutral">
          {t('flash.freshBefore')} <strong>{t('verify')}</strong>. {t('flash.freshAfter')}
        </Flash>
      )}
      {flash === 'return-path-pending' && (
        <Flash tone="amber">
          {t('flash.returnPathPending', { domain: detail.domain })}
        </Flash>
      )}
      {flash === 'return-path-missing' && (
        <Flash tone="amber">
          {t('flash.returnPathMissingBefore', { domain: detail.domain })} <strong>bounces.{detail.domain}</strong>{' '}
          {t('flash.returnPathMissingMiddle')} <strong>{t('verify')}</strong> {t('flash.returnPathMissingAfter')}
        </Flash>
      )}

      {hasBouncesRecords && (
        <p className="mt-3 text-xs leading-relaxed text-gray-500" data-testid="return-path-why">
          {t('returnPathWhyBefore')} <strong>bounces.{detail.domain}</strong> {t('returnPathWhyAfter')}
        </p>
      )}

      {hasRecords && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            {t('zone.bulk')}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyZoneFile}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            >
              {zoneCopied ? t('zone.copied') : t('zone.copy')}
            </button>
            <button
              type="button"
              onClick={downloadZoneFile}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            >
              {t('zone.download')}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
        {detail.dns_records.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-gray-500">
            {detail.type === 'auto-tenant-subdomain' ? (
              <>{t('records.noneManaged')}</>
            ) : (
              <>{t('records.loadFailed')}</>
            )}
          </div>
        ) : (
          detail.dns_records.map((r, i) => <RecordRow key={i} record={r} />)
        )}
      </div>

      {/* OCP-219 — per-domain open-tracking opt-in. */}
      <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 px-3 py-3">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-brand-500"
            checked={detail.open_tracking_enabled}
            disabled={trackingBusy || !canChangeTracking}
            onChange={async (e) => {
              const next = e.target.checked;
              setTrackingBusy(true);
              setTrackingFlash(null);
              try {
                const confirmed = await onSetOpenTracking(next);
                if (confirmed === true) {
                  setTrackingFlash(
                    t('tracking.enabled'),
                  );
                } else if (confirmed === false) {
                  setTrackingFlash(
                    t('tracking.disabled'),
                  );
                }
              } finally {
                setTrackingBusy(false);
              }
            }}
          />
          <span className="text-sm">
            <span className="font-medium text-gray-900 dark:text-gray-100">{t('tracking.title')}</span>
            <span className="ml-1 text-gray-500">{t('tracking.subtitle')}</span>
          </span>
        </label>
        <p className="mt-2 pl-6 text-xs leading-relaxed text-gray-500">
          {t('tracking.explain')}
        </p>
        {trackingFlash && (
          <p
            role="status"
            className="mt-2 pl-6 text-xs font-medium text-emerald-700 dark:text-emerald-400"
          >
            {trackingFlash}
          </p>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {t('currentStatus')} <StatusPill status={detail.status} />
          <VerificationHint
            status={detail.status}
            detail={detail.verification_detail}
            dkimMode={detail.dkim_mode}
          />
          <ReturnPathChip domain={detail} />
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('common:action.close')}
          </button>
          {showVerify && (
            <button
              type="button"
              onClick={onVerify}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
            >
              {t('verifyNow')}
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function RecordRow({ record }: { record: DnsRecord }) {
  const { t } = useTranslation('mail-domains');
  const [copiedField, setCopiedField] = useTransientState<'row' | 'name' | 'value' | null>(null, 1500);
  function copy(value: string, field: 'row' | 'name' | 'value') {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(value);
    setCopiedField(field);
  }
  return (
    <div className="px-4 py-3 text-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="inline-flex rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-1.5 py-0.5 text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-300">
          {record.type}
        </span>
        <button
          type="button"
          onClick={() => copy(`${record.name}\t${record.type}\t${record.value}`, 'row')}
          className="whitespace-nowrap text-xs text-brand-500 hover:underline"
        >
          {copiedField === 'row' ? t('records.copiedRow') : t('records.copyRow')}
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-[8rem_1fr] sm:gap-x-3 sm:gap-y-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:pt-1">
          {t('records.name')}
        </div>
        <div className="flex items-start gap-1.5">
          <code className="block flex-1 break-all font-mono text-xs leading-snug text-gray-800 dark:text-gray-200">
            {record.name}
          </code>
          <CopyIcon
            label="name"
            copied={copiedField === 'name'}
            onClick={() => copy(record.name, 'name')}
          />
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:pt-1">
          {t('records.value')}
        </div>
        <div className="flex items-start gap-1.5">
          <code className="block flex-1 break-all font-mono text-xs leading-snug text-gray-600 dark:text-gray-400">
            {record.value}
          </code>
          <CopyIcon
            label="value"
            copied={copiedField === 'value'}
            onClick={() => copy(record.value, 'value')}
          />
        </div>
      </div>
    </div>
  );
}

function CopyIcon({
  label,
  copied,
  onClick,
}: {
  label: string;
  copied: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('mail-domains');
  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? t('records.copied') : t('records.copyField', { label })}
      aria-label={copied ? t('records.fieldCopied', { label }) : t('records.copyField', { label })}
      className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60 hover:text-gray-700 dark:hover:text-gray-300"
    >
      {copied ? (
        <span className="text-[10px] text-emerald-600">✓</span>
      ) : (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

function Flash({
  tone,
  children,
}: {
  tone: 'green' | 'amber' | 'neutral';
  children: React.ReactNode;
}) {
  const cls =
    tone === 'green'
      ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-200'
      : tone === 'amber'
        ? 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200'
        : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
  return <div className={`rounded-lg border px-3 py-2 text-xs ${cls}`}>{children}</div>;
}

function ConfirmDeleteModal({
  domain,
  onCancel,
  onConfirm,
}: {
  domain: DomainSummary;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('mail-domains');
  return (
    <ModalShell title={t('delete.title', { domain: domain.domain })} titleId="delete-title" onCancel={onCancel}>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('delete.body')}
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('common:action.cancel')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          {t('remove')}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  titleId,
  children,
  onCancel,
  wide,
}: {
  title: string;
  titleId: string;
  children: React.ReactNode;
  onCancel: () => void;
  wide?: boolean;
}) {
  const { t } = useTranslation('mail-domains');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className={`w-full ${wide ? 'max-w-5xl' : 'max-w-lg'} rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl`}
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="truncate text-base font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t('common:action.close')}
            className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
          >
            ✕
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso, iso);
}
